import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Checkbox, Typography } from '@telus-uds/components-web';
import {
  Tr, Td, DetailsCell, Meta, Actions, Confirm, Notice
} from './css';
import StatusPill from './statusPill';
import RunResults from './runResults';
import { isTerminal } from './useAutomationRun';

const COLUMN_COUNT = 5;

const estimate = (ms) => {
  if (!ms) return '—';
  const minutes = Math.round(ms / 60000);
  return minutes <= 1 ? '~1 min' : `~${minutes} min`;
};

const SectionRow = ({
  section, run, frozen, starting, selected, queued, onToggleSelect, onRun
}) => {
  const [confirming, setConfirming] = useState(false);

  // A queued section still holds its previous run, which is terminal — showing
  // those results would read as a result for the run that hasn't started yet.
  const isMine = !!run && !queued;
  const showResults = isMine && isTerminal(run.status);
  const isRunning = isMine && !isTerminal(run.status);
  const hasDetails = confirming || isRunning || showResults;

  const handleStart = () => {
    if (!confirming) {
      setConfirming(true);
      return;
    }
    setConfirming(false);
    onRun(section.id);
  };

  let buttonLabel = 'Run';
  if (starting) buttonLabel = 'Starting…';
  else if (isRunning) buttonLabel = 'Running…';

  // Queued takes priority — it's about to be re-run, so its last result is stale.
  let statusContent;
  if (queued) {
    statusContent = <Meta style={{ margin: 0, color: '#00437d', fontWeight: 600 }}>Queued, waiting its turn</Meta>;
  } else if (isMine) {
    statusContent = (
      <StatusPill status={run.status} since={run.dispatchedAt} durationMs={run.durationMs} />
    );
  } else {
    statusContent = <Meta style={{ margin: 0 }}>Not run</Meta>;
  }

  return (
    <>
      <Tr active={isMine} dimmed={frozen && !isMine && !queued}>
        <Td style={{ textAlign: 'center' }}>
          <Checkbox
            id={`select-${section.id}`}
            name={`select-${section.id}`}
            label=""
            checked={selected}
            inactive={frozen}
            onChange={() => onToggleSelect(section.id)}
          />
        </Td>
        <Td>
          <Typography variant={{ bold: true }}>{section.label}</Typography>
          {section.description && <Meta>{section.description}</Meta>}
        </Td>
        <Td style={{ whiteSpace: 'nowrap' }}>
          <Meta style={{ margin: 0 }}>{estimate(section.estimatedMs)}</Meta>
        </Td>
        <Td style={{ whiteSpace: 'nowrap' }}>
          {statusContent}
        </Td>
        <Td style={{ textAlign: 'right' }}>
          <Actions style={{ justifyContent: 'flex-end' }}>
            {selected ? (
              <Meta style={{ margin: 0 }}>Queued for batch run</Meta>
            ) : (
              <Button
                variant={{ priority: 'high' }}
                type="button"
                onClick={handleStart}
                inactive={frozen || starting}
              >
                {buttonLabel}
              </Button>
            )}
          </Actions>
        </Td>
      </Tr>

      {hasDetails && (
        <tr>
          <DetailsCell colSpan={COLUMN_COUNT}>
            {confirming && !frozen && (
              <Confirm>
                <Typography variant={{ size: 'small' }}>
                   Did you want to run it?
                </Typography>
                <Button variant={{ priority: 'high' }} type="button" onClick={handleStart}>
                  Yes
                </Button>
                <Button variant={{ priority: 'low' }} type="button" onClick={() => setConfirming(false)}>
                  Cancel
                </Button>
              </Confirm>
            )}

            {isRunning && (
            <Notice>
              {run.status === 'queued'
                ? 'Waiting for a CI runner to pick this up…'
                : 'This page updates on its own.'}
              {run.triggeredByName ? ` Started by ${run.triggeredByName}.` : ''}
            </Notice>
            )}

            {showResults && <RunResults run={run} />}
          </DetailsCell>
        </tr>
      )}
    </>
  );
};

SectionRow.propTypes = {
  section: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    description: PropTypes.string,
    estimatedMs: PropTypes.number,
    mutatesData: PropTypes.bool
  }).isRequired,
  run: PropTypes.shape({
    runId: PropTypes.string,
    status: PropTypes.string,
    dispatchedAt: PropTypes.string,
    durationMs: PropTypes.number,
    triggeredByName: PropTypes.string
  }),
  frozen: PropTypes.bool,
  starting: PropTypes.bool,
  selected: PropTypes.bool,
  queued: PropTypes.bool,
  onToggleSelect: PropTypes.func.isRequired,
  onRun: PropTypes.func.isRequired
};

SectionRow.defaultProps = {
  run: null,
  frozen: false,
  starting: false,
  selected: false,
  queued: false
};

export default SectionRow;
